import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SwipeCard from '../components/SwipeCard';
import { getBusinesses, getCategories, getLocations } from '../api';
import { useWishlist } from '../context/WishlistContext';
import {
  Heart, X, SlidersHorizontal, RotateCcw, CheckCircle, ArrowUpRight
} from 'lucide-react';
import { toast } from 'sonner';
import { getProxiedImageUrl } from '../utils/imageProxy';

const PAGE_SIZE = 10;

export default function ScopriPage() {
  const [stack, setStack]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [done, setDone]               = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categories, setCategories]   = useState([]);
  const [locations, setLocations]     = useState(null);
  const [filters, setFilters]         = useState({ categoria: '', citta: '', regione: '', conFoto: false });
  const [seenIds, setSeenIds]         = useState(new Set());
  const [page, setPage]               = useState(1);
  const { toggle, isInWishlist }      = useWishlist();

  useEffect(() => {
    Promise.all([getCategories(), getLocations()])
      .then(([catRes, locRes]) => {
        setCategories(catRes.data || []);
        setLocations(locRes.data || {});
      })
      .catch(() => {});
  }, []);

  const loadMore = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = {
        limit: PAGE_SIZE,
        page: reset ? 1 : page,
        ...(filters.categoria && { categoria: filters.categoria }),
        ...(filters.citta     && { citta: filters.citta }),
        ...(filters.regione   && { regione: filters.regione }),
      };
      const res = await getBusinesses(params);
      const businesses = (res.data?.items || res.data || []);
      let filtered = businesses.filter((b) => !(reset ? new Set() : seenIds).has(b._id));
      if (filters.conFoto) filtered = filtered.filter((b) => b.immagini?.length > 0);
      if (filtered.length === 0 && businesses.length < PAGE_SIZE) {
        setDone(true);
      } else {
        setStack((prev) => (reset ? filtered : [...prev, ...filtered]));
        setSeenIds((prev) => {
          const next = new Set(reset ? [] : prev);
          businesses.forEach((b) => next.add(b._id));
          return next;
        });
        if (!reset) setPage((p) => p + 1);
      }
    } catch {
      toast.error('Errore nel caricamento. Riprova.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, seenIds]);

  useEffect(() => {
    loadMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLike = (business) => {
    if (!isInWishlist(business._id)) {
      toggle(business);
      toast.success(`Salvato: ${business.nome}`, { duration: 2000 });
    }
    removeTop();
  };

  const handleSkip = () => removeTop();

  const removeTop = () => {
    setStack((prev) => {
      const next = prev.slice(1);
      if (next.length <= 2 && !done) loadMore();
      if (next.length === 0 && done) setDone(true);
      return next;
    });
  };

  const handleReset = () => {
    setSeenIds(new Set());
    setPage(1);
    setDone(false);
    setStack([]);
    loadMore(true);
  };

  const applyFilters = () => {
    setFiltersOpen(false);
    setSeenIds(new Set());
    setPage(1);
    setDone(false);
    setStack([]);
    loadMore(true);
  };

  const topBusiness  = stack[0];
  const nextBusiness = stack[1];
  const thirdBusiness = stack[2];
  const fourthBusiness = stack[3];

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100dvh' }}>
      <Header />

      <div className="mi-container">
        {/* ── HERO HEADER ────────────────────────────────── */}
        <div className="pt-10 pb-8 md:pt-16 md:pb-12 relative z-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-6">
            {/* Controls */}
            <div className="flex items-center justify-center gap-4 flex-shrink-0 w-full md:w-auto">
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="group flex items-center justify-center gap-2 rounded-full border text-sm font-semibold transition-all hover:scale-[1.02]"
                style={{
                  padding: '0 24px',
                  height: '56px',
                  borderColor: filtersOpen ? 'var(--mi-accent)' : 'rgba(26,26,26,0.12)',
                  backgroundColor: filtersOpen ? 'var(--mi-accent-light)' : '#fff',
                  color: '#1A1A1A',
                  boxShadow: filtersOpen ? 'none' : '0 4px 12px rgba(26,26,26,0.03)',
                }}
              >
                <SlidersHorizontal size={15} />
                Filtri
              </button>
              <Link
                to="/preferiti"
                className="group flex items-center justify-center gap-2 rounded-full text-sm font-bold transition-all hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(26,26,26,0.15)]"
                style={{
                  padding: '0 24px',
                  height: '56px',
                  backgroundColor: '#1A1A1A',
                  color: '#FDFBF7',
                }}
              >
                <Heart size={14} />
                Preferiti
                <span
                  className="flex items-center justify-center rounded-full ml-1"
                  style={{
                    width: 24, height: 24,
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    transition: 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
                  }}
                >
                  <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        {/* ── FILTERS PANEL ──────────────────────────────── */}
        {filtersOpen && (
          <div
            className="mb-10 animate-fade-in"
            style={{
              padding: '1.5rem',
              borderRadius: '1.5rem',
              backgroundColor: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(26,26,26,0.08)',
              boxShadow: '0 8px 32px rgba(26,26,26,0.06)',
            }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  label: 'Categoria',
                  content: (
                    <select
                      value={filters.categoria}
                      onChange={(e) => setFilters((p) => ({ ...p, categoria: e.target.value }))}
                      style={selectStyle}
                    >
                      <option value="">Tutte</option>
                      {categories.map((c) => (
                        <option key={c.slug} value={c.slug}>{c.nome}</option>
                      ))}
                    </select>
                  )
                },
                {
                  label: 'Regione',
                  content: (
                    <select
                      value={filters.regione}
                      onChange={(e) => setFilters((p) => ({ ...p, regione: e.target.value, citta: '' }))}
                      style={selectStyle}
                    >
                      <option value="">Tutte</option>
                      {(locations?.regioni || []).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  )
                },
                {
                  label: 'Città',
                  content: (
                    <input
                      value={filters.citta}
                      onChange={(e) => setFilters((p) => ({ ...p, citta: e.target.value }))}
                      placeholder="Es. Milano"
                      style={{ ...selectStyle, outline: 'none' }}
                    />
                  )
                },
              ].map(({ label, content }) => (
                <div key={label}>
                  <label style={labelStyle}>{label}</label>
                  {content}
                </div>
              ))}

              <div className="flex flex-col justify-end gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.conFoto}
                    onChange={(e) => setFilters((p) => ({ ...p, conFoto: e.target.checked }))}
                    style={{ accentColor: 'var(--mi-accent)', width: 15, height: 15 }}
                  />
                  <span style={{ fontSize: 13, color: '#1A1A1A' }}>Solo con foto</span>
                </label>
                <button
                  onClick={applyFilters}
                  className="btn-gold rounded-full text-sm font-medium"
                  style={{ height: 42, transition: 'all 0.4s cubic-bezier(0.32,0.72,0,1)' }}
                >
                  Applica filtri
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── CARD STACK ─────────────────────────────────── */}
        <div className="flex flex-col items-center pb-24">

          {/* Stack container */}
          <div className="relative w-full max-w-[380px]" style={{ height: 620 }}>

            {/* Loading skeleton */}
            {loading && stack.length === 0 && (
              <div
                className="absolute inset-0 skeleton-shimmer"
                style={{ borderRadius: '2rem', boxShadow: '0 32px 80px rgba(26,26,26,0.14)' }}
              />
            )}

            {/* Done state */}
            {done && stack.length === 0 && !loading && (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center text-center p-10 animate-scale-in"
                style={{
                  borderRadius: '2rem',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(26,26,26,0.07)',
                  boxShadow: '0 32px 80px rgba(26,26,26,0.10)',
                }}
              >
                <div
                  className="flex items-center justify-center mb-6"
                  style={{
                    width: 72, height: 72,
                    borderRadius: '50%',
                    backgroundColor: 'var(--mi-accent-light)',
                  }}
                >
                  <CheckCircle size={32} style={{ color: 'var(--mi-accent)' }} />
                </div>
                <h3
                  className="font-display mb-2"
                  style={{ fontSize: '1.6rem', color: '#1A1A1A', letterSpacing: '-0.02em' }}
                >
                  Hai visto tutto!
                </h3>
                <p style={{ fontSize: 13, color: '#6B6860', lineHeight: 1.6, marginBottom: '1.5rem' }}>
                  Hai esplorato tutte le attività disponibili.
                </p>
                <button
                  onClick={handleReset}
                  className="group flex items-center gap-2 rounded-full font-medium text-sm"
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#1A1A1A',
                    color: '#FDFBF7',
                    transition: 'all 0.4s cubic-bezier(0.32,0.72,0,1)',
                  }}
                >
                  <RotateCcw size={14} />
                  Ricomincia
                </button>
              </div>
            )}

            {/* Fourth card (deepest) */}
            {fourthBusiness && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  borderRadius: '1.5rem',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(26,26,26,0.05)',
                  transform: 'scale(0.82) translateY(36px)',
                  zIndex: 0,
                  boxShadow: '0 4px 16px rgba(26,26,26,0.03)',
                  transition: 'all 0.35s cubic-bezier(0.32,0.72,0,1)',
                  opacity: 0.4,
                }}
              >
                {fourthBusiness.immagini?.[0] && (
                  <>
                    <img src={getProxiedImageUrl(fourthBusiness.immagini[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)' }} />
                  </>
                )}
              </div>
            )}

            {/* Third card */}
            {thirdBusiness && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  borderRadius: '1.5rem',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(26,26,26,0.06)',
                  transform: 'scale(0.88) translateY(24px)',
                  zIndex: 1,
                  boxShadow: '0 8px 24px rgba(26,26,26,0.05)',
                  transition: 'all 0.35s cubic-bezier(0.32,0.72,0,1)',
                  opacity: 0.7,
                }}
              >
                {thirdBusiness.immagini?.[0] && (
                  <>
                    <img src={getProxiedImageUrl(thirdBusiness.immagini[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(2px)' }} />
                  </>
                )}
              </div>
            )}

            {/* Second card */}
            {nextBusiness && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{
                  borderRadius: '1.5rem',
                  backgroundColor: '#1a1a1a',
                  border: '1px solid rgba(26,26,26,0.07)',
                  transform: 'scale(0.94) translateY(12px)',
                  zIndex: 2,
                  boxShadow: '0 16px 40px rgba(26,26,26,0.08)',
                  transition: 'all 0.35s cubic-bezier(0.32,0.72,0,1)',
                  opacity: 0.9,
                }}
              >
                {nextBusiness.immagini?.[0] && (
                  <>
                    <img src={getProxiedImageUrl(nextBusiness.immagini[0])} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(1px)' }} />
                  </>
                )}
              </div>
            )}

            {/* Top card */}
            {topBusiness && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 3 }}>
                <SwipeCard
                  key={topBusiness._id}
                  business={topBusiness}
                  onLike={handleLike}
                  onSkip={handleSkip}
                  isTop
                />
              </div>
            )}
          </div>

          {/* ── ACTION BUTTONS ─────────────────────────── */}
          {!done && stack.length > 0 && (
            <div className="flex items-center gap-6 mt-12 z-10 relative">

              {/* Skip */}
              <button
                onClick={handleSkip}
                title="Salta"
                className="group flex items-center justify-center transition-all hover:-translate-y-1 active:scale-95 active:translate-y-0"
                style={{
                  width: 56, height: 56,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: '1.5px solid rgba(239,68,68,0.2)',
                  boxShadow: '0 8px 24px rgba(239,68,68,0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(239,68,68,0.6)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(239,68,68,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(239,68,68,0.2)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(239,68,68,0.1)';
                }}
              >
                <X size={22} style={{ color: '#ef4444' }} />
              </button>

              {/* Like — button-in-button architecture */}
              <button
                onClick={() => topBusiness && handleLike(topBusiness)}
                title="Salva nei preferiti"
                className="group flex items-center justify-center transition-all hover:-translate-y-1 active:scale-95 active:translate-y-0"
                style={{
                  width: 72, height: 72,
                  borderRadius: '50%',
                  backgroundColor: 'var(--mi-accent)',
                  boxShadow: '0 12px 32px rgba(200,169,110,0.4)',
                  border: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 16px 40px rgba(200,169,110,0.55)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(200,169,110,0.4)';
                }}
              >
                <Heart size={30} fill="#fff" style={{ color: '#fff' }} />
              </button>

              {/* Reset */}
              <button
                onClick={handleReset}
                title="Ricomincia"
                className="group flex items-center justify-center transition-all hover:-translate-y-1 active:scale-95 active:translate-y-0"
                style={{
                  width: 44, height: 44,
                  borderRadius: '50%',
                  backgroundColor: '#fff',
                  border: '1.5px solid rgba(26,26,26,0.1)',
                  boxShadow: '0 4px 12px rgba(26,26,26,0.05)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--mi-accent)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(200,169,110,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(26,26,26,0.1)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(26,26,26,0.05)';
                }}
              >
                <RotateCcw size={16} style={{ color: '#6B6860' }} />
              </button>
            </div>
          )}

          {/* Count */}
          {stack.length > 0 && (
            <p
              className="mt-5"
              style={{
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#9B9590',
              }}
            >
              {stack.length} attività rimanenti
            </p>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

const selectStyle = {
  width: '100%',
  height: 42,
  padding: '0 12px',
  borderRadius: 12,
  border: '1px solid rgba(26,26,26,0.12)',
  backgroundColor: '#FDFBF7',
  color: '#1A1A1A',
  fontSize: 13,
};

const labelStyle = {
  display: 'block',
  fontSize: 10,
  textTransform: 'uppercase',
  letterSpacing: '0.18em',
  fontWeight: 600,
  color: '#9B9590',
  marginBottom: 6,
};
